import { usePolledCounts, type PolledCounts } from '@vello/web-theme';
import { type OpsCountKey } from '@vello/api-client';
import { countsFromView, marketsOfRegion, REGION_READ_MAX_MARKETS, sumNavCounts, type NavCount } from '../../src/index';
import { useAdmin } from '../session';

/**
 * The numbers behind the menu badges and the bell (D22 NAV-COUNTS, D560): ONE `GET /v1/ops/counts` for the scope (a city, or —
 * at country / platform scope — the sum over every city the operator may list), every 60 s while the tab is visible (a hidden tab asks nothing), and again when the scope or the grants change.
 *
 * The server counts a queue only for an operator who may list it, with the list's own scope, so a key it did not return is
 * absent — never a zero. A failed or refused read leaves no numbers at all (`status: 'failed'`): no badge, and the bell says
 * "could not read" instead of a reassuring zero. The 15 s SOS watcher (`useOperatorAlerts`) is a separate loop and is not
 * replaced by this one.
 */
export type OpsCountsState = PolledCounts<OpsCountKey, NavCount>;

export function useOpsCounts(): OpsCountsState {
  const { runtime, scope, grants, markets } = useAdmin();
  const market = scope.current.city;
  const country = scope.current.country;
  const region = market === undefined ? scope.current.region : undefined;
  return usePolledCounts<OpsCountKey, NavCount>(
    async () => {
      if (region !== undefined) {
        // A state (ADR-0124): the route has no region filter and a country read would be the whole country, so each market of the
        // state is read (authorized at that city) and the numbers are added; a market that did not answer makes them lower bounds.
        const ids = marketsOfRegion(region, markets).map((m) => m.id).slice(0, REGION_READ_MAX_MARKETS);
        if (ids.length === 0) return {}; // a state with no registered market has nothing waiting
        const answers = await Promise.all(ids.map((id) => runtime.ops.counts(id)));
        const ok = answers.flatMap((a) => (a.ok ? [countsFromView(a.value)] : []));
        return ok.length === 0 ? undefined : sumNavCounts(ok, ok.length < answers.length);
      }
      // A city in the scope: that city. A country (or the whole platform): the server sums every city the operator may list.
      const res = await runtime.ops.counts(market, country);
      return res.ok ? countsFromView(res.value) : undefined;
    },
    // The grants change what the server answers; the market changes which queue is read.
    { resetKey: `${country ?? ''}|${region ?? ''}|${market ?? ''}|${region !== undefined ? markets.length : 0}|${grants === undefined ? 'unknown' : JSON.stringify(grants)}` },
  );
}
